import { LoadingOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  notification,
  Row,
  Segmented,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import useHttp from "../../../hooks/use-http";
import StatusBadge from "./StatusBadge";
import moment from "moment";
import GradientButton from "../../../component/common/GradientButton";
import { CalendarClock, Send, Trash2 } from "lucide-react";
import CustomCard from "./CustomCard";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  apiGenerator,
  convertLocalToUTC,
  convertUTCToLocal,
} from "../../../util/functions";
import CONSTANTS from "../../../util/constant/CONSTANTS";
import { useWritePost } from "./WritePostContext";
import dayjs from "dayjs";
import postValidationRules, {
  getNoChartIcon,
  socialMediaIcons,
} from "../../../util/image";
import LineChart from "../analytics/LineChart";
import CustomDateRange from "../../../component/common/Custom-date-range";

const optionsTab = [
  { label: "Posts", value: "posts" },
  { label: "Analytics", value: "analytics" },
];

const chartTitles = {
  followers: "Followers",
  comments: "Comments",
  likes: "Likes",
  views: "Views",
  impressions: "Impressions",
  impressionsUnique: "Unique Impressions",
  viewsUnique: "Unique Views",
};

const PostDetails = () => {
  const API = useHttp();
  const APIDRAFT = useHttp();
  const location = useLocation();
  const { setManualPost, manualPost, setRefersh, date, setDate } =
    useWritePost();
  const { postId, teamId } = useParams();
  const [currentTab, setCurrentTab] = useState("posts");
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({});
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
  });
  const navigate = useNavigate();
  const postedInProgres =
    "SCHEDULED" === manualPost?.status &&
    manualPost?.postDate &&
    moment(manualPost.postDate).isBefore(moment())
      ? "PROCESSING"
      : manualPost?.status;

  useEffect(() => {
    let intervalId;

    if (postedInProgres === "PROCESSING") {
      intervalId = setInterval(() => {
        setRefersh((prev) => !prev);
      }, 7000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [postedInProgres]);

  const validatePosts = (clickType) => {
    const valueObject = { ...manualPost?.data };
    let allValid = true;

    const checkValidation = Object.keys(valueObject).reduce((acc, platform) => {
      const rules = postValidationRules[platform]?.required;
      if (!rules) {
        acc[platform] = valueObject[platform];
        return acc;
      }

      let isValid = true;

      rules.forEach((rule) => {
        if (typeof rule === "string") {
          if (rule === "media") {
            if (!valueObject[platform]?.uploadIds?.length) {
              isValid = false;
            }
          } else if (!valueObject[platform]?.[rule]) {
            isValid = false;
          }
        } else if (rule.or) {
          const hasAtLeastOne = rule.or.some((key) => {
            if (key === "media") {
              return valueObject[platform]?.uploadIds?.length;
            }
            return valueObject[platform]?.[key];
          });

          if (!hasAtLeastOne) {
            isValid = false;
          }
        }
      });

      acc[platform] = {
        ...valueObject[platform],
        ...(isValid ? { validation: undefined } : { validation: false }), // Remove validation key if valid
      };

      if (isValid) {
        delete acc[platform].validation;
      } else allValid = false;

      return acc;
    }, {});

    if (!allValid) setManualPost((pre) => ({ ...pre, data: checkValidation }));

    if (!allValid) return;

    let payload = {
      data: checkValidation,
    };

    if (clickType === "Schedule") {
      payload = {
        ...payload,
        status: "SCHEDULED",
        postDate: date, // Use selected date
      };
    } else if (clickType === "PostNow") {
      payload = {
        ...payload,
        status: "SCHEDULED",
        postDate: convertLocalToUTC(moment()), // Convert current time to UTC
      };
    } else {
      payload = {
        ...payload,
        status: "DRAFT", // Default status if missing
        postDate: date || convertLocalToUTC(moment()), // Use existing or current UTC time
      };
    }

    updatePost(payload);
  };

  const onChange = (selectedD) => {
    // if (date) {
    //   const utcDate = convertLocalToUTC(date.$d);
    //   if (utcDate) {
    //     setManualPost((pre) => ({
    //       ...pre,
    //       postDate: utcDate,
    //     }));
    //   }
    // } else {
    //   setManualPost((pre) => {
    //     const { postDate, ...rest } = pre;
    //     return { ...rest };
    //   });
    // }
    if (selectedD) {
      const utcDate = convertLocalToUTC(selectedD.$d);
      if (utcDate) {
        setDate(utcDate);
      }
    } else {
      setDate(null);
    }
  };

  const updatePost = (payload) => {
    const apiInstance = payload?.status === "DRAFT" ? APIDRAFT : API;

    apiInstance.sendRequest(
      apiGenerator(CONSTANTS.API.post.updateOne, { postId: manualPost?.id }),
      (res) => {
        setRefersh((pre) => !pre);
      },
      payload
    );
  };

  const makeCopy = () => {
    API.sendRequest(
      apiGenerator(CONSTANTS.API.post.nakeCopy, { postId: manualPost?.id }),
      (res) => {
        if (res?.data?.socialBundlePostId)
          navigate(
            location.pathname.replace(/[^/]+$/, res?.data?.socialBundlePostId)
          );
      },
      {},
      "Post copied successfully."
    );
  };

  const onDelete = () => {
    API.sendRequest(
      apiGenerator(CONSTANTS.API.post.deletePost, { postId }),
      (res) => {
        navigate(`/team/${teamId}/posts`);
      },
      {},
      "Post Deleted successfully."
    );
  };

  const chartHeader = ({ followersCount, label }) => {
    return (
      <div className="flex flex-col items-start gap-1">
        <div className="font-semibold text-2xl text-[#020817]">{`${label}: ${followersCount}`}</div>
        <div className="font-normal text-base text-[#6b7280]">
          Graph showing the number of {label?.toLowerCase()} over time.
        </div>
      </div>
    );
  };

  const getAnalyticsData = () => {
    API.sendRequest(
      apiGenerator(
        CONSTANTS.API.analytics.getByPostId,
        {},
        `?postId=${manualPost?.id}&platformType=${selectedPlatform}${
          dates?.startDate && dates?.endDate
            ? `&startDate=${dates.startDate}&endDate=${dates.endDate}`
            : ""
        }`
      ),
      (res) => {
        if (res?.analytics?.length) {
          const chartData = {
            followers: { data: [] },
            comments: { data: [] },
            likes: { data: [] },
            views: { data: [] },
            impressions: { data: [] },
            impressionsUnique: { data: [] },
            viewsUnique: { data: [] },
          };

          // Create a lookup for dailyChangeAnalytics by date
          const dailyChangeMap = {};
          res?.dailyChangeAnalytics?.forEach((d) => {
            const dateKey = convertUTCToLocal(d?.createdAt, "MMMM, D");
            dailyChangeMap[dateKey] = d;
          });

          res?.analytics?.reverse()?.forEach((item) => {
            const date = convertUTCToLocal(item?.createdAt, "MMMM, D");
            const dailyChange = dailyChangeMap[date] || {};

            chartData.followers.data.push({
              date,
              count: item?.followers ?? 0,
              difference: dailyChange?.followers ?? 0,
            });

            chartData.comments.data.push({ 
              date, 
              count: item?.comments ?? 0,
              difference: dailyChange?.comments ?? 0,
            });

            chartData.likes.data.push({ 
              date, 
              count: item?.likes ?? 0,
              difference: dailyChange?.likes ?? 0,
            });

            chartData.views.data.push({ 
              date, 
              count: item?.views ?? 0,
              difference: dailyChange?.views ?? 0,
            });

            chartData.impressions.data.push({
              date,
              count: item?.impressions ?? 0,
              difference: dailyChange?.impressions ?? 0,
            });

            chartData.impressionsUnique.data.push({
              date,
              count: item?.impressionsUnique ?? 0,
              difference: dailyChange?.impressionsUnique ?? 0,
            });

            chartData.viewsUnique.data.push({
              date,
              count: item?.viewsUnique ?? 0,
              difference: dailyChange?.viewsUnique ?? 0,
            });
          });
          setAnalyticsData(chartData);
        } else setAnalyticsData({});
      },
      {},
      null,
      (err) => {
        setAnalyticsData({});
        notification.error({
          message: err,
          duration: "3",
        });
      }
    );
  };

  useEffect(() => {
    if (!manualPost?.id || !selectedPlatform || currentTab !== "analytics")
      return;
    getAnalyticsData();
  }, [selectedPlatform, manualPost?.id, dates]);

  useEffect(() => {
    if (
      currentTab === "analytics" &&
      Object.keys(analyticsData || {}).length === 0 &&
      selectedPlatform
    ) {
      getAnalyticsData();
    }
  }, [currentTab]);

  useEffect(() => {
    const postedPlatform = Object.keys(manualPost?.data || {})?.filter(
      (item) => !["LINKEDIN", "TWITTER"].includes(item)
    );
    if (!postedPlatform?.length) return;

    setSelectedPlatform(postedPlatform[0]);
    setConnectedAccounts(postedPlatform);
  }, [manualPost?.data]);

  return (
    <Spin
      spinning={API?.isLoading}
      indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
    >
      <Row className="w-full flex md:flex-row flex-col justify-between gap-4 mb-8">
        <div className="text-center">
          {moment(manualPost?.postedDate).isBefore(moment()) ? (
            <Segmented
              value={currentTab}
              options={optionsTab}
              onChange={(option) => setCurrentTab(option)}
              size="large"
              className="border-solid border-2 border-[#a855f7] rounded-lg w-fit !bg-transparent"
              style={{
                padding: "5px",
              }}
            />
          ) : (
            <div className="text-xl font-semibold text-left">Posts</div>
          )}
        </div>

        {!["PROCESSING", "POSTED", "ERROR", "DELETED"].includes(
          postedInProgres
        ) && (
          <div className="flex flex-wrap items-center gap-4">
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              placeholder="Schedule Post"
              value={date && dayjs(date)}
              disabledDate={(current) =>
                current && current < moment().startOf("day")
              }
              popupClassName="in-mobile"
              className="sm:max-w-52 w-full"
              onChange={onChange}
              suffixIcon={<CalendarClock size={22} color="#828997" />}
            />
            <div className="flex w-full md:w-auto gap-4">
              <GradientButton
                buttonText={
                  date && moment(date).isAfter(moment())
                    ? "Schedule"
                    : "Post Now"
                }
                className="w-1/2 md:w-auto px-6"
                onClick={() =>
                  validatePosts(
                    date && moment(date).isAfter(moment())
                      ? "Schedule"
                      : "PostNow"
                  )
                }
                loading={API.isLoading}
                icon={
                  date && moment(date).isAfter(moment()) ? (
                    ""
                  ) : (
                    <Send strokeWidth={1.5} color="#fff" />
                  )
                }
              />
              <Button
                type="default"
                onClick={() => validatePosts("Update")}
                className="w-1/2 md:w-auto px-6 h-[50px]"
                loading={APIDRAFT.isLoading}
              >
                Update
              </Button>
            </div>
          </div>
        )}
      </Row>
      {currentTab === "posts" && (
        <>
          <div className="columns-1 md:columns-2 xl:columns-3 gap-5 space-y-5">
            {Object.keys(manualPost?.data || {}).map((sm) => (
              <div key={sm} className="break-inside-avoid">
                <CustomCard card={sm} />
              </div>
            ))}
          </div>
          <Card className="p-2 md:rounded-[30px] rounded-[20px] mt-[30px] mb-5">
            <div className="flex justify-between items-center">
              <StatusBadge status={postedInProgres} />
              <div className="text-base font-normal text-[#6b7280]">
                {moment
                  .utc(manualPost?.postDate)
                  .local()
                  .format("D MMMM YYYY, hh:mm A")}
              </div>
            </div>
            {manualPost?.postrequest?.prompt && (
              <pre
                className="font-normal text-lg text-[#020817] text-wrap mb-0"
                style={{ fontFamily: "Inter" }}
              >
                {manualPost?.postrequest?.prompt}
              </pre>
            )}
          </Card>
          {postedInProgres !== "PROCESSING" && (
            <Row className="w-full flex md:flex-row flex-col mt-[30px] mb-5 gap-4">
              <GradientButton
                buttonText="Make A Copy"
                // disabled={!manualPost?.postDate}
                className="min-w-52 sm:w-auto"
                onClick={makeCopy}
              />
              {manualPost?.status !== "DELETED" && (
                <Button
                  type="default"
                  onClick={onDelete}
                  icon={
                    <Trash2
                      size={23}
                      color="#6b7280"
                      strokeWidth={1.5}
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  }
                  className="min-w-52 sm:w-auto h-[50px]"
                >
                  Delete
                </Button>
              )}
            </Row>
          )}
        </>
      )}
      {currentTab === "analytics" && (
        <>
          <div className="md:mb-[30px] mb-5 flex justify-between items-center flex-wrap gap-4">
            <div className="flex flex-wrap items-center gap-4 ">
              {connectedAccounts?.map((md) => (
                <Avatar
                  key={`selectedplatform_${md}`}
                  src={socialMediaIcons?.[md]}
                  size={44}
                  onClick={() => setSelectedPlatform(md)}
                  className={`${
                    selectedPlatform === md
                      ? "border-[#a855f7] bg-[#faf5ff]"
                      : "border-[#d9dbdf]"
                  } border-[2px] p-2 bg-transparent cursor-pointer`}
                />
              ))}
            </div>

            <CustomDateRange dates={dates} setDates={setDates} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 xxl:grid-cols-3 gap-[20px] md:gap-[30px] min-h-24 w-full">
            {Object.entries(analyticsData).map(([key, { data }]) => (
              <Card
                key={key}
                className="rounded-[30px]"
                styles={{
                  body: { padding: 30 },
                }}
              >
                <div className="flex flex-col gap-4">
                  {chartHeader({
                    followersCount: data[data?.length - 1]?.count ?? 0,
                    label: chartTitles[key],
                  })}
                  <LineChart chartData={data} hoverLabel={chartTitles[key]} />
                </div>
              </Card>
            ))}
          </div>
          {Object.keys(analyticsData)?.length === 0 && (
            <Empty image={getNoChartIcon()} description="No Posts, No Stats" />
          )}
        </>
      )}
    </Spin>
  );
};

export default PostDetails;
